import { ReactElement, useCallback, useMemo } from 'react'
import { SelectNative } from '@alto-avios/alto-ui'
import { checkDisabledTime } from '@reward-platform/lift/utils/datetime'
import { TimePickerProps } from './interface'

export function TimePicker({
  value,
  label,
  setTime,
  errors,
  helperMessage,
  chosenDate,
  availability,
  minDateTime,
  locale,
  isLastAvailableRentalDay,
}: TimePickerProps): ReactElement<React.PropsWithChildren> {
  const [hours, minutes] = useMemo(() => value?.split(':'), [value]) || ['00', '00']

  const createTimeOptions = useCallback(() => {
    const arr: string[] = []
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 4; j++) {
        const mins = j === 0 ? `00` : 15 * j
        arr.push(`${i.toLocaleString(locale, { minimumIntegerDigits: 2 })}:${mins}`)
      }
    }

    const isTimeAvailable = (timeValue: string) =>
      !checkDisabledTime(
        locale,
        timeValue,
        chosenDate,
        availability,
        minDateTime,
        isLastAvailableRentalDay
      )

    return arr.filter(isTimeAvailable).map((timeValue) => ({
      label: timeValue,
      value: timeValue,
    }))
  }, [locale, chosenDate, availability, minDateTime, isLastAvailableRentalDay])

  const timeOptions = useMemo(() => createTimeOptions(), [createTimeOptions])
  const hasNoTimeOptions = timeOptions.length === 0

  const hasErrors = !!errors?.length
  const isDisabled = hasNoTimeOptions && !helperMessage
  const selectValue = hasNoTimeOptions ? undefined : `${hours}:${minutes}`
  const errorMessage = hasErrors ? errors[0] : ''
  const shouldShowHelperMessage = !hasErrors && hasNoTimeOptions && !!helperMessage
  const description = shouldShowHelperMessage ? helperMessage : undefined

  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTime(e.currentTarget.value)
  }

  return (
    <SelectNative
      options={timeOptions}
      value={selectValue}
      isRequired
      isDisabled={isDisabled}
      onChange={handleTimeChange}
      label={label}
      description={description}
      isInvalid={hasErrors}
      errorMessage={errorMessage}
      data-testid="timepicker-listbox-container"
    />
  )
}
